import { useState, useRef } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReplayIcon from '@mui/icons-material/Replay';

// ─── Constants ────────────────────────────────────────────────────────────────
const DIM = 'rgba(6, 15, 34, 0.90)';
const PAD = 12;
const Z   = { dim: 9999, panels: 10000, glow: 10001, ghost: 10002, ui: 10003, cursor: 10004 };

// ─── Cursor SVGs ──────────────────────────────────────────────────────────────
const ArrowSVG = () => (
  <svg width="26" height="36" viewBox="0 0 26 36" fill="none">
    <path d="M1 1L1 27L8 20L13 33.5L16 32L11 19H20L1 1Z"
      fill="white" stroke="#1e3a5f" strokeWidth="2" strokeLinejoin="round"/>
  </svg>
);
const GrabSVG = () => (
  <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
    <rect x="5" y="18" width="24" height="14" rx="5" fill="white" stroke="#1e3a5f" strokeWidth="1.8"/>
    <rect x="8"  y="8"  width="5" height="13" rx="2.5" fill="white" stroke="#1e3a5f" strokeWidth="1.7"/>
    <rect x="14" y="6"  width="5" height="15" rx="2.5" fill="white" stroke="#1e3a5f" strokeWidth="1.7"/>
    <rect x="20" y="7"  width="5" height="14" rx="2.5" fill="white" stroke="#1e3a5f" strokeWidth="1.7"/>
    <rect x="26" y="11" width="4" height="10" rx="2"   fill="white" stroke="#1e3a5f" strokeWidth="1.7"/>
  </svg>
);

// ─── Spotlight ────────────────────────────────────────────────────────────────
const Spotlight = ({ rect: r }) => (
  <>
    <Box sx={{ position:'fixed', left:0, top:0, right:0, height: Math.max(0, r.top - PAD), bgcolor: DIM, zIndex: Z.panels }} />
    <Box sx={{ position:'fixed', left:0, top: r.bottom + PAD, right:0, bottom:0, bgcolor: DIM, zIndex: Z.panels }} />
    <Box sx={{ position:'fixed', top: r.top - PAD, left:0, width: Math.max(0, r.left - PAD), height: r.height + PAD * 2, bgcolor: DIM, zIndex: Z.panels }} />
    <Box sx={{ position:'fixed', top: r.top - PAD, left: r.right + PAD, right:0, height: r.height + PAD * 2, bgcolor: DIM, zIndex: Z.panels }} />
    <Box sx={{
      position:'fixed',
      left: r.left - PAD, top: r.top - PAD,
      width: r.width + PAD * 2, height: r.height + PAD * 2,
      border: '2.5px solid rgba(230,196,94,0.92)',
      borderRadius: '14px',
      boxShadow: '0 0 0 5px rgba(230,196,94,0.12), 0 0 40px rgba(230,196,94,0.4)',
      pointerEvents: 'none',
      zIndex: Z.glow,
      animation: 'goldPulse 2s ease-in-out infinite',
      '@keyframes goldPulse': {
        '0%,100%': { boxShadow: '0 0 0 5px rgba(230,196,94,0.12), 0 0 40px rgba(230,196,94,0.4)' },
        '50%':     { boxShadow: '0 0 0 8px rgba(230,196,94,0.2),  0 0 60px rgba(230,196,94,0.55)' },
      },
    }} />
  </>
);

// ─── Ghost widget (floats on top of real widget, moves/resizes) ───────────────
const GhostWidget = ({ rect: r, transX, transY, extraW, extraH }) => (
  <Box sx={{
    position: 'fixed',
    left: r.left,
    top:  r.top,
    width:  r.width  + extraW,
    height: r.height + extraH,
    transform: `translate(${transX}px, ${transY}px)`,
    transition: 'transform 0.9s cubic-bezier(0.25,0.8,0.25,1), width 0.9s cubic-bezier(0.25,0.8,0.25,1), height 0.9s cubic-bezier(0.25,0.8,0.25,1)',
    bgcolor: 'rgba(20,45,90,0.45)',
    border: '2.5px dashed rgba(255,255,255,0.85)',
    borderRadius: '12px',
    boxShadow: '0 22px 60px rgba(0,0,0,0.55)',
    backdropFilter: 'blur(4px)',
    overflow: 'hidden',
    zIndex: Z.ghost,
    pointerEvents: 'none',
  }}>
    {/* Ghost header */}
    <Box sx={{ height: 34, bgcolor: 'rgba(30,58,95,0.75)', display:'flex', alignItems:'center', px:1.5, gap:0.7 }}>
      {[1,2,3].map(i => <Box key={i} sx={{ width:7, height:7, borderRadius:'50%', bgcolor:'rgba(255,255,255,0.3)' }} />)}
    </Box>
    {/* Ghost body */}
    <Box sx={{ p:1.5, display:'flex', flexDirection:'column', gap:1 }}>
      {[72,48,62,38].map((w,i) => (
        <Box key={i} sx={{ height:8, width:`${w}%`, bgcolor:'rgba(255,255,255,0.18)', borderRadius:1 }} />
      ))}
    </Box>
    {/* Resize handle corner */}
    <Box sx={{ position:'absolute', bottom:4, right:4, width:14, height:14, borderRight:'2.5px solid rgba(255,255,255,0.7)', borderBottom:'2.5px solid rgba(255,255,255,0.7)' }} />
  </Box>
);

// ─── Bottom label ─────────────────────────────────────────────────────────────
const LabelBar = ({ title, sub }) => (
  <Box sx={{
    position:'fixed', top:36, left:'50%', transform:'translateX(-50%)',
    textAlign:'center', zIndex: Z.ui, pointerEvents:'none',
    px: 3,
    animation: 'labelIn 0.5s ease',
    '@keyframes labelIn': {
      from: { opacity:0, transform:'translateX(-50%) translateY(-14px)' },
      to:   { opacity:1, transform:'translateX(-50%) translateY(0)' },
    },
  }}>
    <Typography sx={{ fontWeight:900, color:'white', fontSize:'2.6rem', lineHeight:1.2, textShadow:'0 3px 16px rgba(0,0,0,0.55)', mb:1.2 }}>
      {title}
    </Typography>
    <Typography sx={{ color:'rgba(255,255,255,0.82)', fontSize:'1.3rem', fontWeight:500, lineHeight:1.55, maxWidth:560 }}>
      {sub}
    </Typography>
  </Box>
);

// ─── Free-space info card ─────────────────────────────────────────────────────
const FreeSpaceCard = () => (
  <Box sx={{
    position:'fixed', top:'50%', left:'50%',
    transform:'translate(-50%,-50%)',
    zIndex: Z.ui, bgcolor:'white', borderRadius:4,
    p: 5, width: 620, textAlign:'center',
    boxShadow:'0 28px 70px rgba(0,0,0,0.45)',
    border: '2px solid rgba(230,168,23,0.35)',
    animation: 'cardIn 0.5s ease',
    '@keyframes cardIn': {
      from:{ opacity:0, transform:'translate(-50%,-50%) scale(0.9)' },
      to:  { opacity:1, transform:'translate(-50%,-50%) scale(1)' },
    },
  }}>
    <Box sx={{
      display:'flex', justifyContent:'center', mb:2.5,
      '@keyframes iconShake': {
        '0%,100%': { transform:'rotate(0deg) scale(1)' },
        '15%':     { transform:'rotate(-8deg) scale(1.12)' },
        '30%':     { transform:'rotate(8deg) scale(1.12)' },
        '45%':     { transform:'rotate(-5deg) scale(1.08)' },
        '60%':     { transform:'rotate(5deg) scale(1.08)' },
        '75%':     { transform:'rotate(-2deg) scale(1.04)' },
      },
    }}>
      <WarningAmberIcon sx={{
        fontSize: 68, color: '#e6a817',
        filter: 'drop-shadow(0 4px 12px rgba(230,168,23,0.5))',
        animation: 'iconShake 2.6s ease-in-out infinite',
      }} />
    </Box>
    <Typography variant="h5" sx={{ fontWeight:800, color:'#1e3a5f', mb:2.5, lineHeight:1.4, fontSize:'1.65rem' }}>
      חשוב לדעת — הלוח מלא
    </Typography>
    <Typography sx={{ color:'#4a5568', fontSize:'1.18rem', lineHeight:1.95 }}>
      ניתן להזיז רכיב <strong>רק אם יש מקום פנוי</strong> בלוח.
      <br/>
      אם הלוח מלא — <strong>הקטן רכיב אחר</strong> (גרור את הפינה שלו פנימה)
      <br/>
      או <strong>מחק רכיב שאינך צריך</strong>, ואז תוכל לגרור.
    </Typography>
  </Box>
);

// ─── Summary / ending card ────────────────────────────────────────────────────
const EndingCard = ({ onReplay, onTry, isDemo }) => (
  <Box sx={{
    position:'fixed', top:'50%', left:'50%',
    transform:'translate(-50%,-50%)',
    zIndex: Z.ui, bgcolor:'white', borderRadius:4,
    px: 6, py: 5, width: 520, textAlign:'center',
    boxShadow:'0 28px 70px rgba(0,0,0,0.45)',
    animation: 'cardIn 0.55s ease',
    '@keyframes cardIn': {
      from:{ opacity:0, transform:'translate(-50%,-50%) scale(0.88)' },
      to:  { opacity:1, transform:'translate(-50%,-50%) scale(1)' },
    },
  }}>
    <Typography variant="h4" sx={{ fontWeight:900, color:'#1e3a5f', mb:3 }}>
      סיכום קצר
    </Typography>

    {/* Bullet list */}
    <Box sx={{ textAlign:'right', mb:4, display:'flex', flexDirection:'column', gap:1.5 }}>
      {[
        'גרור רכיב מכל מקום להזזה',
        'גרור פינה או שוליים לשינוי גודל',
        'ריחוף מעל רכיב פותח עריכה ומחיקה',
        'הלוח מלא? הקטן או מחק רכיב לפני גרירה',
      ].map((item, i) => (
        <Box key={i} sx={{ display:'flex', alignItems:'flex-start', gap:1.5 }}>
          <Box sx={{
            minWidth: 26, height: 26, borderRadius:'50%',
            bgcolor:'#1e3a5f', color:'white',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:'0.8rem', fontWeight:800, mt:0.2,
          }}>
            {i + 1}
          </Box>
          <Typography sx={{ color:'#334155', fontSize:'1.05rem', lineHeight:1.6, fontWeight:500 }}>
            {item}
          </Typography>
        </Box>
      ))}
    </Box>

    {/* Demo-only note */}
    {isDemo && (
      <Box sx={{
        bgcolor:'rgba(30,58,95,0.05)', border:'1px solid rgba(30,58,95,0.14)',
        borderRadius:3, px:3, py:2, mb:3, textAlign:'right',
      }}>
        <Typography sx={{ color:'#1e3a5f', fontSize:'0.92rem', lineHeight:1.8, fontWeight:500 }}>
          <strong>טעימה בלבד — </strong>
          בפלטפורמה המלאה תמצא עוד רכיבים, לוחות מרובים, תזמון תוכן, הגדרות מתקדמות ועוד.
        </Typography>
      </Box>
    )}

    {/* Action buttons */}
    <Box sx={{ display:'flex', gap:2, justifyContent:'center' }}>
      <Button
        variant="outlined"
        onClick={onReplay}
        startIcon={<ReplayIcon />}
        sx={{
          borderColor:'#1e3a5f', color:'#1e3a5f',
          borderRadius:3, px:3.5, py:1.2,
          fontWeight:700, fontSize:'1rem',
          '&:hover':{ bgcolor:'rgba(30,58,95,0.06)' },
        }}
      >
        הצג שוב
      </Button>
      <Button
        variant="contained"
        onClick={onTry}
        sx={{
          bgcolor:'#1e3a5f', color:'white',
          borderRadius:3, px:4, py:1.2,
          fontWeight:800, fontSize:'1rem',
          boxShadow:'0 4px 16px rgba(30,58,95,0.4)',
          '&:hover':{ bgcolor:'#2d5a8f' },
        }}
      >
        נסה בעצמך! ←
      </Button>
    </Box>
  </Box>
);

// ─── Step definitions ─────────────────────────────────────────────────────────
const STEPS = [
  {
    title: 'גרור להזזה',
    sub:   'תפוס מכל מקום על הרכיב — לא רק בכותרת',
  },
  {
    title: 'גרור פינה לשינוי גודל',
    sub:   'תפוס פינה או שוליים ומשוך',
  },
  {
    title: 'ריחוף לעריכה ומחיקה',
    sub:   'העבר עכבר מעל רכיב לראות אפשרויות עריכה',
  },
];

// ─── Initial animation state ──────────────────────────────────────────────────
const INIT_ANIM = {
  showIntro:        false,
  spotlight:        false,
  showGhost:        false,
  ghostTransX:      0,
  ghostTransY:      0,
  ghostExtraW:      0,
  ghostExtraH:      0,
  cursorX:          0,
  cursorY:          0,
  grabbing:         false,
  click:            false,
  showEditCallout:  false,
  editBtnPressed:   false,
  deleteBtnPressed: false,
  showFreeSpace:    false,
  labelIdx:         -1,
  ending:           false,
};

// ─── Main component ───────────────────────────────────────────────────────────
const TOTAL_STEPS = 4;

const BoardOnboarding = ({ onClose, isDemo = false }) => {
  const [phase, setPhase]               = useState('entry');
  const [step, setStep]                 = useState(0);
  const [anim, setAnim]                 = useState(INIT_ANIM);
  const [showContinueBtn, setShowContinueBtn] = useState(false);

  const widgetRect  = useRef(null);
  const waypointsRef = useRef(null);
  const timers      = useRef([]);
  const stepRef     = useRef(-1);

  const upd      = (patch) => setAnim(prev => ({ ...prev, ...patch }));
  const schedule = (fn, ms) => { const id = setTimeout(fn, ms); timers.current.push(id); };
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  // ── Navigation ──────────────────────────────────────────────────────────────
  const handleClose = () => {
    clearTimers();
    stepRef.current = -1;
    setAnim(INIT_ANIM);
    setShowContinueBtn(false);
    setPhase('entry');
    setStep(0);
    onClose?.();
  };

  const handleContinue = () => {
    const next = stepRef.current + 1;
    goToStep(next, widgetRect.current, waypointsRef.current);
  };

  const handleReplay = () => {
    clearTimers();
    stepRef.current = -1;
    setAnim(INIT_ANIM);
    setShowContinueBtn(false);
    setStep(0);
    _startIntroThenStep(widgetRect.current, waypointsRef.current);
  };

  // ── Step runners (each loops until user clicks "הבנתי") ─────────────────────
  const runDragStep = (r, wp, si) => {
    if (stepRef.current !== si) return;
    setAnim({ ...INIT_ANIM, spotlight: true, cursorX: wp.grab.x, cursorY: wp.grab.y });
    schedule(() => upd({ showGhost: true }), 120);
    schedule(() => upd({ grabbing: true, click: true }), 360);
    schedule(() => upd({ click: false }), 580);
    schedule(() => upd({
      ghostTransX: wp.dragTo.x - wp.grab.x,
      ghostTransY: wp.dragTo.y - wp.grab.y,
      cursorX: wp.dragTo.x, cursorY: wp.dragTo.y,
    }), 820);
    schedule(() => upd({ labelIdx: 0 }), 1500);
    schedule(() => upd({ showGhost: false, grabbing: false, labelIdx: -1, cursorX: wp.grab.x, cursorY: wp.grab.y }), 2600);
    // Show button + schedule loop (button stays visible during loop)
    schedule(() => {
      if (stepRef.current !== si) return;
      setShowContinueBtn(true);
      schedule(() => {
        if (stepRef.current !== si) return;
        runDragStep(r, wp, si);   // button stays — no hide
      }, 2000);
    }, 2800);
  };

  const runResizeStep = (r, wp, si) => {
    if (stepRef.current !== si) return;
    setAnim({ ...INIT_ANIM, spotlight: true, cursorX: wp.corner.x, cursorY: wp.corner.y });
    schedule(() => upd({ showGhost: true }), 120);
    schedule(() => upd({ grabbing: true, click: true }), 360);
    schedule(() => upd({ click: false }), 580);
    schedule(() => upd({
      ghostExtraW: r ? Math.min(120, r.width  * 0.4) : 110,
      ghostExtraH: r ? Math.min(85,  r.height * 0.4) : 80,
      cursorX: wp.resizeTo.x, cursorY: wp.resizeTo.y,
    }), 820);
    schedule(() => upd({ labelIdx: 1 }), 1500);
    schedule(() => upd({ showGhost: false, grabbing: false, labelIdx: -1, cursorX: wp.hover.x, cursorY: wp.hover.y }), 2600);
    schedule(() => {
      if (stepRef.current !== si) return;
      setShowContinueBtn(true);
      schedule(() => {
        if (stepRef.current !== si) return;
        runResizeStep(r, wp, si);
      }, 2000);
    }, 2800);
  };

  const runHoverStep = (r, wp, si) => {
    if (stepRef.current !== si) return;
    setAnim({ ...INIT_ANIM, spotlight: true, cursorX: wp.hover.x, cursorY: wp.hover.y });
    schedule(() => upd({ showEditCallout: true, labelIdx: 2 }), 200);
    schedule(() => upd({ cursorX: wp.editBtn.x, cursorY: wp.editBtn.y }), 650);
    schedule(() => upd({ editBtnPressed: true, click: true }), 1150);
    schedule(() => upd({ editBtnPressed: false, click: false }), 1450);
    schedule(() => upd({ cursorX: wp.deleteBtn.x, cursorY: wp.deleteBtn.y }), 1900);
    schedule(() => upd({ deleteBtnPressed: true, click: true }), 2400);
    schedule(() => upd({ deleteBtnPressed: false, click: false }), 2700);
    schedule(() => upd({ labelIdx: -1 }), 3000);
    schedule(() => {
      if (stepRef.current !== si) return;
      setShowContinueBtn(true);
      schedule(() => {
        if (stepRef.current !== si) return;
        runHoverStep(r, wp, si);
      }, 2000);
    }, 3200);
  };

  const runFreeSpaceStep = (si) => {
    if (stepRef.current !== si) return;
    setAnim({ ...INIT_ANIM, showFreeSpace: true });
    // No loop — static card; show button after reading time
    schedule(() => {
      if (stepRef.current !== si) return;
      setShowContinueBtn(true);
    }, 1200);
  };

  // ── Step dispatcher ─────────────────────────────────────────────────────────
  const goToStep = (si, r, wp) => {
    clearTimers();
    setShowContinueBtn(false);
    stepRef.current = si;
    setStep(si);
    if      (si === 0) runDragStep(r, wp, 0);
    else if (si === 1) runResizeStep(r, wp, 1);
    else if (si === 2) runHoverStep(r, wp, 2);
    else if (si === 3) runFreeSpaceStep(3);
    else               setAnim({ ...INIT_ANIM, ending: true });
  };

  // ── Intro → step 0 ──────────────────────────────────────────────────────────
  const _startIntroThenStep = (r, wp) => {
    clearTimers();
    setPhase('playing');
    setAnim({ ...INIT_ANIM, showIntro: true });
    schedule(() => goToStep(0, r, wp), 1400);
  };

  // ── Entry point (called by play button) ─────────────────────────────────────
  const playAnimation = () => {
    const el = document.querySelector('.react-grid-item:not(.react-grid-placeholder)');
    const r  = el?.getBoundingClientRect() ?? null;
    widgetRect.current = r;

    let grab, dragTo, corner, resizeTo, hover, editBtn, deleteBtn;
    if (r) {
      grab     = { x: r.left + r.width * 0.44, y: r.top + r.height * 0.33 };
      dragTo   = { x: grab.x + Math.min(100, r.width * 0.3), y: grab.y + Math.min(70, r.height * 0.3) };
      corner   = { x: r.right - 10, y: r.bottom - 10 };
      resizeTo = { x: r.right + 60, y: r.bottom + 50 };
      hover    = { x: r.left + r.width * 0.5, y: r.top + r.height * 0.5 };
      editBtn   = { x: r.right - 40, y: r.top + 22 };
      deleteBtn = { x: r.right - 40, y: r.top + 62 };
    } else {
      const cx = window.innerWidth * 0.5, cy = window.innerHeight * 0.4;
      grab = { x: cx, y: cy }; dragTo = { x: cx + 100, y: cy + 70 };
      corner = { x: cx + 150, y: cy + 110 }; resizeTo = { x: cx + 215, y: cy + 165 };
      hover = { x: cx, y: cy }; editBtn = { x: cx + 80, y: cy - 40 }; deleteBtn = { x: cx + 80, y: cy };
    }
    const wp = { grab, dragTo, corner, resizeTo, hover, editBtn, deleteBtn };
    waypointsRef.current = wp;
    _startIntroThenStep(r, wp);
  };

  // ── Destructure ─────────────────────────────────────────────────────────────
  const {
    showIntro, spotlight, showGhost,
    ghostTransX, ghostTransY, ghostExtraW, ghostExtraH,
    cursorX, cursorY, grabbing, click,
    showEditCallout, editBtnPressed, deleteBtnPressed,
    showFreeSpace, labelIdx, ending,
  } = anim;

  const r = widgetRect.current;

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: Z.dim, direction: 'rtl' }}>

      {/* ═══ ENTRY CARD ═══════════════════════════════════════════════════════ */}
      {phase === 'entry' && (
        <>
          <Box sx={{ position:'absolute', inset:0, bgcolor: DIM, backdropFilter:'blur(3px)' }}
            onClick={handleClose} />

          <Box sx={{
            position:'absolute', top:'50%', left:'50%',
            transform:'translate(-50%,-50%)',
            bgcolor:'white', borderRadius:4,
            px:6, py:5.5, textAlign:'center', width:480,
            boxShadow:'0 28px 70px rgba(0,0,0,0.44)',
            animation:'cardUp 0.4s ease',
            '@keyframes cardUp': {
              from:{ opacity:0, transform:'translate(-50%,-50%) translateY(22px)' },
              to:  { opacity:1, transform:'translate(-50%,-50%) translateY(0)' },
            },
          }}>
            <IconButton onClick={handleClose} size="small"
              sx={{ position:'absolute', top:12, left:12, color:'#bbb', '&:hover':{ color:'#555' } }}>
              <CloseIcon />
            </IconButton>

            <Box sx={{
              display:'flex', justifyContent:'center', mb:3,
              '@keyframes bigPulse':{ '0%,100%':{ transform:'scale(1)', opacity:.82 }, '50%':{ transform:'scale(1.1)', opacity:1 } },
              '& svg':{ animation:'bigPulse 2s ease-in-out infinite', color:'#1e3a5f' },
            }}>
              <PlayCircleOutlineIcon sx={{ fontSize:100 }} />
            </Box>

            <Typography variant="h4" sx={{ fontWeight:900, color:'#1e3a5f', mb:1.5 }}>
              לחץ לצפייה בהדרכה
            </Typography>
            <Typography sx={{ color:'#7a8a9a', fontSize:'1.1rem', lineHeight:1.8, mb:4.5 }}>
              תראה איך לגרור, לשנות גודל, לערוך —
              <br/>
              וגם מה לעשות כשהלוח מלא
            </Typography>

            <Button variant="contained" size="large" onClick={playAnimation} sx={{
              bgcolor:'#1e3a5f', color:'white', borderRadius:3, px:6, py:1.7,
              fontWeight:800, fontSize:'1.15rem',
              boxShadow:'0 6px 20px rgba(30,58,95,0.42)',
              '&:hover':{ bgcolor:'#2d5a8f', boxShadow:'0 8px 26px rgba(30,58,95,0.52)' },
            }}>
              ▶&nbsp;&nbsp;הצג הדרכה
            </Button>

            <Typography onClick={handleClose} sx={{
              display:'block', mt:3, color:'#c0c8d4', fontSize:'0.92rem',
              cursor:'pointer', '&:hover':{ color:'#777' },
            }}>
              דלג על ההדרכה ←
            </Typography>
          </Box>
        </>
      )}

      {/* ═══ ANIMATION ════════════════════════════════════════════════════════ */}
      {phase === 'playing' && (
        <>
          {/* Full dim */}
          {(!spotlight || showFreeSpace || ending) && (
            <Box sx={{ position:'fixed', inset:0, bgcolor: DIM, zIndex: Z.panels, backdropFilter:'blur(2px)' }} />
          )}

          {/* Spotlight */}
          {spotlight && !showFreeSpace && !ending && r && <Spotlight rect={r} />}

          {/* Ghost widget */}
          {showGhost && r && (
            <GhostWidget rect={r} transX={ghostTransX} transY={ghostTransY} extraW={ghostExtraW} extraH={ghostExtraH} />
          )}

          {/* Cursor */}
          {!ending && (
            <Box sx={{
              position:'fixed', left: cursorX, top: cursorY,
              transition:'left 0.9s cubic-bezier(0.25,0.8,0.25,1), top 0.9s cubic-bezier(0.25,0.8,0.25,1)',
              zIndex: Z.cursor, filter:'drop-shadow(0 3px 8px rgba(0,0,0,0.55))', pointerEvents:'none',
            }}>
              {grabbing ? <GrabSVG /> : <ArrowSVG />}
              {click && (
                <Box sx={{
                  position:'absolute', top:0, left:0, width:24, height:24,
                  borderRadius:'50%', bgcolor:'rgba(255,255,255,0.55)',
                  animation:'ripple 0.45s ease-out forwards',
                  '@keyframes ripple':{ from:{ transform:'scale(0)', opacity:1 }, to:{ transform:'scale(5)', opacity:0 } },
                }} />
              )}
            </Box>
          )}

          {/* Intro */}
          {showIntro && (
            <Box sx={{
              position:'fixed', top:'38%', left:'50%', transform:'translate(-50%,-50%)',
              textAlign:'center', zIndex: Z.ui, px:4,
              animation:'introIn 0.55s ease',
              '@keyframes introIn':{ from:{ opacity:0, transform:'translate(-50%,-50%) translateY(-18px)' }, to:{ opacity:1, transform:'translate(-50%,-50%) translateY(0)' } },
            }}>
              <Typography sx={{ fontWeight:800, color:'white', fontSize:'2.2rem', lineHeight:1.6, textShadow:'0 3px 18px rgba(0,0,0,0.55)', maxWidth:700 }}>
                הלוח מאפשר לגרור, לשנות גודל ולערוך רכיבים
              </Typography>
            </Box>
          )}

          {/* Edit / delete callout */}
          {showEditCallout && r && (
            <Box sx={{
              position:'fixed', top: r.top + 8, right: Math.max(10, window.innerWidth - r.right + 8),
              display:'flex', flexDirection:'column', alignItems:'flex-end', gap:0.5, zIndex: Z.ui,
              animation:'btnIn 0.35s ease',
              '@keyframes btnIn':{ from:{ opacity:0, transform:'translateY(-8px)' }, to:{ opacity:1, transform:'translateY(0)' } },
            }}>
              {[
                { baseColor:'#1e3a5f', pressedColor:'#0a1f3a', icon:<EditIcon sx={{ fontSize:17, color:'white' }}/>, label:'עריכה', pressed: editBtnPressed },
                { baseColor:'#c0392b', pressedColor:'#8b1a13', icon:<DeleteOutlineIcon sx={{ fontSize:17, color:'white' }}/>, label:'מחיקה', pressed: deleteBtnPressed },
              ].map(({ baseColor, pressedColor, icon, label, pressed }) => (
                <Box key={label} sx={{
                  bgcolor: pressed ? pressedColor : baseColor,
                  borderRadius:'7px', px:1.8, py:0.9,
                  display:'flex', alignItems:'center', gap:0.8,
                  boxShadow:'0 4px 14px rgba(0,0,0,0.35)',
                  transform: pressed ? 'scale(0.92)' : 'scale(1)',
                  transition: 'transform 0.12s ease, background-color 0.12s ease',
                }}>
                  {icon}
                  <Typography sx={{ fontSize:'0.92rem', color:'white', fontWeight:700 }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Step label — always visible during steps 0-2 */}
          {!showIntro && !showFreeSpace && !ending && step <= 2 && (
            <LabelBar title={STEPS[step].title} sub={STEPS[step].sub} />
          )}

          {/* Free space card */}
          {showFreeSpace && <FreeSpaceCard />}

          {/* Ending card */}
          {ending && <EndingCard onReplay={handleReplay} onTry={handleClose} isDemo={isDemo} />}

          {/* ── Bottom controls ── */}
          {!ending && (
            <Box sx={{
              position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)',
              zIndex: Z.ui, display:'flex', flexDirection:'column', alignItems:'center', gap:1.2,
            }}>
              {showContinueBtn && (
                <>
                  <Typography sx={{ color:'rgba(255,255,255,0.85)', fontSize:'1.05rem', fontWeight:700, letterSpacing:'0.04em', textShadow:'0 2px 8px rgba(0,0,0,0.5)' }}>
                    שלב {step + 1} מתוך {TOTAL_STEPS}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleContinue}
                    sx={{
                      bgcolor:'#e6a817', color:'#1a1200',
                      borderRadius:3, px:5.5, py:1.5,
                      fontWeight:900, fontSize:'1.35rem',
                      boxShadow:'0 8px 28px rgba(230,168,23,0.6)',
                      animation:'btnPop 0.4s cubic-bezier(0.34,1.56,0.64,1)',
                      '@keyframes btnPop':{ from:{ opacity:0, transform:'scale(0.78)' }, to:{ opacity:1, transform:'scale(1)' } },
                      '&:hover':{ bgcolor:'#f0b820', transform:'scale(1.04)' },
                      transition:'background-color 0.15s, transform 0.15s',
                    }}
                  >
                    הבנתי, עבור הלאה ←
                  </Button>
                </>
              )}
              <Button onClick={handleClose} sx={{
                color:'rgba(255,255,255,0.22)', fontSize:'0.82rem',
                '&:hover':{ color:'rgba(255,255,255,0.6)', bgcolor:'transparent' },
              }}>
                דלג על ההדרכה
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default BoardOnboarding;
